import { inject, Injectable, PLATFORM_ID, TransferState } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay, startWith, switchMap, timer } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  FEATURE_STATUS_STATE_KEY,
  FeatureStatusMap,
} from './feature-status-state';

@Injectable({
  providedIn: 'root',
})
export class BdFeatureStatusService {
  private readonly _httpClient = inject(HttpClient);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _transferState = inject(TransferState);
  private readonly _cache: Observable<FeatureStatusMap>;

  constructor() {
    if (isPlatformBrowser(this._platformId)) {
      const seed = this._readAndConsumeTransferState();
      // When the SSR layer has pre-populated TransferState, the first emission
      // comes from the transferred value and we defer the network poll by a
      // full interval so we don't immediately re-fetch what we already have.
      const pollStart = seed !== null ? 60 * 1_000 : 0;
      const polled$ = timer(pollStart, 60 * 1_000).pipe(
        switchMap(() => this._fetchFeatureStates()),
      );
      this._cache = (
        seed !== null ? polled$.pipe(startWith(seed)) : polled$
      ).pipe(shareReplay(1));
    } else {
      this._cache = this._fetchFeatureStates().pipe(shareReplay(1));
    }
  }

  public getFeatureStatus(
    featureIds: string[],
  ): Observable<FeatureStatusMap> {
    return this._cache.pipe(
      map((cache) => {
        const result: FeatureStatusMap = { ...cache };
        for (const featureId of featureIds) {
          if (!(featureId in result)) {
            result[featureId] = true;
          }
        }
        return result;
      }),
    );
  }

  private _fetchFeatureStates(): Observable<FeatureStatusMap> {
    return this._httpClient.get<FeatureStatusMap>('/feature-status.json');
  }

  private _readAndConsumeTransferState(): FeatureStatusMap | null {
    if (!this._transferState.hasKey(FEATURE_STATUS_STATE_KEY)) {
      return null;
    }
    const value = this._transferState.get(FEATURE_STATUS_STATE_KEY, {});
    this._transferState.remove(FEATURE_STATUS_STATE_KEY);
    return value;
  }
}
